import { useState, useMemo, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface AvailabilityRow {
  id: string
  date: string
  status: 'available' | 'unavailable' | 'tentative'
  blockedBy: string | null
  note: string | null
}

// 12-month window starting from first of current month
function buildDateRange(): { fromISO: string; toISO: string } {
  const start = new Date()
  start.setDate(1)
  const end = new Date(start)
  end.setMonth(end.getMonth() + 12)
  end.setDate(0) // last day of month 11
  return {
    fromISO: start.toISOString().slice(0, 10),
    toISO: end.toISOString().slice(0, 10),
  }
}

const { fromISO, toISO } = buildDateRange()

// Returns cells for a 6-row calendar grid (Sun–Sat).
// Empty strings represent padding cells from previous/next month.
function monthGrid(year: number, monthIdx: number): (string | null)[] {
  const firstDay = new Date(year, monthIdx, 1).getDay() // 0=Sun
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate()
  const cells: (string | null)[] = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const mm = String(monthIdx + 1).padStart(2, '0')
    const dd = String(d).padStart(2, '0')
    cells.push(`${year}-${mm}-${dd}`)
  }
  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const STATUS_BG: Record<string, string> = {
  available: 'bg-emerald-500/20 border-emerald-500 text-emerald-800',
  tentative: 'bg-amber-500/20 border-amber-500 text-amber-800',
  unavailable: 'bg-rose-500/20 border-rose-500 text-rose-800',
}

const STATUS_CHIP: Record<string, string> = {
  available: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  tentative: 'bg-amber-100 text-amber-700 border-amber-300',
  unavailable: 'bg-rose-100 text-rose-700 border-rose-300',
}

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

const TODAY_ISO = toISODate(new Date())

interface RangeModalProps {
  onClose: () => void
  onApply: (from: string, to: string, status: 'unavailable' | 'tentative' | 'available') => Promise<void>
}

function RangeModal({ onClose, onApply }: RangeModalProps) {
  const [dateFrom, setDateFrom] = useState(TODAY_ISO)
  const [dateTo, setDateTo] = useState(TODAY_ISO)
  const [status, setStatus] = useState<'unavailable' | 'tentative' | 'available'>('unavailable')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (dateFrom > dateTo) { setErr('From must be before To.'); return }
    setSubmitting(true)
    setErr(null)
    try {
      await onApply(dateFrom, dateTo, status)
      onClose()
    } catch (ex) {
      setErr(ex instanceof ApiClientError ? ex.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-xl border border-ovation-ink/10 bg-ovation-paper p-6 shadow-xl">
        <h2 className="mb-4 font-display text-lg font-semibold text-ovation-ink">
          Range Select
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm text-ovation-ink focus:outline-none focus:ring-2 focus:ring-ovation-accent/50"
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm text-ovation-ink focus:outline-none focus:ring-2 focus:ring-ovation-accent/50"
              />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs uppercase tracking-wider text-ovation-muted">Status</p>
            <div className="flex gap-3">
              {(['unavailable', 'tentative', 'available'] as const).map((s) => (
                <label key={s} className="flex cursor-pointer items-center gap-1.5 text-sm text-ovation-ink">
                  <input
                    type="radio"
                    name="status"
                    value={s}
                    checked={status === s}
                    onChange={() => setStatus(s)}
                    className="accent-ovation-accent"
                  />
                  <span className="capitalize">{s}</span>
                </label>
              ))}
            </div>
          </div>

          {err && <p className="text-sm text-rose-600">{err}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-ovation-ink/15 px-4 py-2 text-sm text-ovation-ink hover:bg-ovation-ink/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Apply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

interface MonthCalendarProps {
  year: number
  monthIdx: number
  statusMap: Map<string, string>
  toggling: string | null
  onDayClick: (iso: string, current: string | undefined) => void
}

function MonthCalendar({ year, monthIdx, statusMap, toggling, onDayClick }: MonthCalendarProps) {
  const cells = useMemo(() => monthGrid(year, monthIdx), [year, monthIdx])
  const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
    .format(new Date(year, monthIdx, 1))

  return (
    <div className="mb-8">
      <h2 className="mb-3 font-display text-xl font-semibold text-ovation-ink">{monthLabel}</h2>
      {/* Weekday headers */}
      <div className="mb-1 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="text-center text-[10px] font-medium uppercase tracking-wider text-ovation-muted">
            {wd}
          </div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((iso, idx) => {
          if (iso === null) {
            return <div key={`pad-${idx}`} />
          }
          const isPast = iso < TODAY_ISO
          const isToday = iso === TODAY_ISO
          const status = statusMap.get(iso)
          const isToggling = toggling === iso

          const baseClasses = 'relative flex h-8 w-full items-center justify-center rounded text-xs border select-none sm:h-9'
          const colourClasses = status ? STATUS_BG[status] : 'border-ovation-ink/10 bg-white text-ovation-ink'
          const pastClasses = isPast ? 'opacity-40' : 'cursor-pointer hover:ring-2 hover:ring-ovation-accent/60'
          const todayClasses = isToday ? 'ring-2 ring-ovation-accent' : ''
          const togglingClasses = isToggling ? 'opacity-60' : ''

          return (
            <button
              key={iso}
              disabled={isPast || isToggling}
              onClick={() => onDayClick(iso, status)}
              aria-label={`${iso}${status ? ` – ${status}` : ''}`}
              className={`${baseClasses} ${colourClasses} ${pastClasses} ${todayClasses} ${togglingClasses}`}
            >
              {String(new Date(iso + 'T00:00:00').getDate())}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function CrewAvailabilityPage() {
  const { data, error, isLoading, refetch } = useQuery<AvailabilityRow[], ApiClientError>({
    queryKey: ['crew-availability', fromISO, toISO],
    queryFn: () =>
      apiFetch<AvailabilityRow[]>(
        `/crew/profile/availability?from=${fromISO}&to=${toISO}`,
      ),
  })

  const statusMap = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>()
    data?.forEach((row) => m.set(row.date, row.status))
    return m
  }, [data])

  const [toggling, setToggling] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)

  async function toggleDay(dateISO: string, current: string | undefined) {
    // Cycle: undefined → unavailable → tentative → available → (treated as cleared / available)
    const next: 'unavailable' | 'tentative' | 'available' =
      !current ? 'unavailable'
      : current === 'unavailable' ? 'tentative'
      : 'available'

    setToggling(dateISO)
    try {
      await apiFetch('/crew/profile/availability/bulk', {
        method: 'POST',
        body: { dateFrom: dateISO, dateTo: dateISO, status: next },
      })
      await refetch()
    } finally {
      setToggling(null)
    }
  }

  async function applyRange(
    dateFrom: string,
    dateTo: string,
    status: 'unavailable' | 'tentative' | 'available',
  ) {
    await apiFetch('/crew/profile/availability/bulk', {
      method: 'POST',
      body: { dateFrom, dateTo, status },
    })
    await refetch()
  }

  // Build 12 month slots starting from current month
  const months = useMemo(() => {
    const today = new Date()
    const result: { year: number; monthIdx: number }[] = []
    for (let i = 0; i < 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1)
      result.push({ year: d.getFullYear(), monthIdx: d.getMonth() })
    }
    return result
  }, [])

  return (
    <div>
      <PageHeader
        title="Availability"
        subtitle="Block out days you can't work — holidays, hospital trips, existing bookings. Artists see only your available days."
      />

      {/* Legend + range button */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        {[
          { label: 'Available', cls: STATUS_CHIP.available },
          { label: 'Tentative', cls: STATUS_CHIP.tentative },
          { label: 'Unavailable', cls: STATUS_CHIP.unavailable },
          { label: 'Past', cls: 'bg-ovation-ink/5 text-ovation-muted border-ovation-ink/10' },
        ].map(({ label, cls }) => (
          <span
            key={label}
            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${cls}`}
          >
            {label}
          </span>
        ))}
        <button
          onClick={() => setShowModal(true)}
          className="ml-auto rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium text-ovation-ink hover:bg-ovation-ink/5"
        >
          Range select →
        </button>
      </div>

      <AsyncBoundary isLoading={isLoading} error={error}>
        <div>
          {months.map(({ year, monthIdx }) => (
            <MonthCalendar
              key={`${year}-${monthIdx}`}
              year={year}
              monthIdx={monthIdx}
              statusMap={statusMap}
              toggling={toggling}
              onDayClick={toggleDay}
            />
          ))}
        </div>
      </AsyncBoundary>

      {showModal && (
        <RangeModal
          onClose={() => setShowModal(false)}
          onApply={applyRange}
        />
      )}
    </div>
  )
}
